import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Banner,
  Divider,
  SkeletonBodyText,
  SettingToggle,
  Badge as PolarBadge,
} from "@shopify/polaris";
import { DEFAULT_SETTINGS, TIER_NAMES, POINTS_UNIT, PROGRAM_NAME } from "../../shared/types.js";
import { getSettings, updateSettings } from "../lib/api.js";

export default function Settings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [programEnabled, setProgramEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const data = await getSettings();
      setSettings(data);
      if (data.programEnabled !== undefined) {
        setProgramEnabled(data.programEnabled);
      }
    } catch {
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateSettings({ ...settings, programEnabled });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updatePointRate(tier, value) {
    setSettings((prev) => ({
      ...prev,
      pointsPerHundredTL: {
        ...prev.pointsPerHundredTL,
        [tier]: parseInt(value, 10) || 0,
      },
    }));
  }

  function updateTierThreshold(tier, value) {
    setSettings((prev) => ({
      ...prev,
      tierThresholds: {
        ...prev.tierThresholds,
        [tier]: parseInt(value, 10) || 0,
      },
    }));
  }

  function updateBonusPoints(key, value) {
    setSettings((prev) => ({
      ...prev,
      bonusPoints: {
        ...prev.bonusPoints,
        [key]: parseInt(value, 10) || 0,
      },
    }));
  }

  function updateCouponTier(index, field, value) {
    setSettings((prev) => {
      const newTiers = [...prev.couponTiers];
      newTiers[index] = { ...newTiers[index], [field]: parseInt(value, 10) || 0 };
      return { ...prev, couponTiers: newTiers };
    });
  }

  function addCouponTier() {
    setSettings((prev) => ({
      ...prev,
      couponTiers: [...prev.couponTiers, { points: 0, discountTL: 0 }],
    }));
  }

  function removeCouponTier(index) {
    setSettings((prev) => ({
      ...prev,
      couponTiers: prev.couponTiers.filter((_, i) => i !== index),
    }));
  }

  if (loading) {
    return (
      <Page title="Ayarlar">
        <Layout>
          <Layout.Section>
            <Card><SkeletonBodyText lines={10} /></Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Ayarlar"
      subtitle={`${PROGRAM_NAME} parametrelerini yönet`}
      primaryAction={{
        content: saving ? "Kaydediliyor..." : "Kaydet",
        onAction: handleSave,
        loading: saving,
      }}
    >
      {saved && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="success" onDismiss={() => setSaved(false)}>
            Ayarlar basariyla kaydedildi.
          </Banner>
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="critical" onDismiss={() => setError(null)}>
            Hata: {error}
          </Banner>
        </div>
      )}

      <Layout>
        {/* Program Aktif/Kapalı Toggle */}
        <Layout.Section>
          <SettingToggle
            action={{
              content: programEnabled ? "Devre Dışı Bırak" : "Aktifleştir",
              onAction: () => setProgramEnabled(!programEnabled),
              destructive: programEnabled,
            }}
            enabled={programEnabled}
          >
            <InlineStack gap="200" blockAlign="center">
              <Text variant="bodyMd" as="span">
                {PROGRAM_NAME} şu an{" "}
                <PolarBadge tone={programEnabled ? "success" : "critical"}>
                  {programEnabled ? "Aktif" : "Kapalı"}
                </PolarBadge>
              </Text>
            </InlineStack>
            <Text variant="bodySm" tone="subdued" as="p">
              {programEnabled
                ? "Program aktif. Müşteriler puan kazanıyor, kupon oluşturuyor ve mini oyunları oynayabiliyor."
                : "Program kapalı. Storefront'ta hiçbir loyalty bileşeni görünmüyor. Puan kazanımı ve kupon oluşturma durdu."}
            </Text>
          </SettingToggle>
        </Layout.Section>

        {/* Points Per 100 TL */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Puan Kazanim Orani
              </Text>
              <Text variant="bodySm" tone="subdued">
                Her 100 TL harcama başına kazanılan {POINTS_UNIT}
              </Text>
              <Divider />
              {["rookie", "pro", "legend"].map((tier) => (
                <TextField
                  key={tier}
                  label={`${TIER_NAMES[tier]} Seviye`}
                  type="number"
                  value={String(settings.pointsPerHundredTL?.[tier] || 0)}
                  onChange={(v) => updatePointRate(tier, v)}
                  autoComplete="off"
                  suffix={`${POINTS_UNIT} / 100 TL`}
                />
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Tier Thresholds */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Tier Esikleri
              </Text>
              <Text variant="bodySm" tone="subdued">
                Seviye yukseltme icin gereken toplam harcama (TL)
              </Text>
              <Divider />
              {["rookie", "pro", "legend"].map((tier) => (
                <TextField
                  key={tier}
                  label={`${TIER_NAMES[tier]} Seviye`}
                  type="number"
                  value={String(settings.tierThresholds?.[tier] || 0)}
                  onChange={(v) => updateTierThreshold(tier, v)}
                  autoComplete="off"
                  suffix="TL"
                  disabled={tier === "rookie"}
                />
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Bonus Points */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Bonus Puanlar
              </Text>
              <Text variant="bodySm" tone="subdued">
                Ozel aksiyonlar icin verilen bonus puanlar
              </Text>
              <Divider />
              <TextField
                label="Hesap Olusturma"
                type="number"
                value={String(settings.bonusPoints?.registration || 0)}
                onChange={(v) => updateBonusPoints("registration", v)}
                autoComplete="off"
                suffix={POINTS_UNIT}
              />
              <TextField
                label="Ilk Siparis"
                type="number"
                value={String(settings.bonusPoints?.firstOrder || 0)}
                onChange={(v) => updateBonusPoints("firstOrder", v)}
                autoComplete="off"
                suffix={POINTS_UNIT}
              />
              <TextField
                label="Dogum Gunu (Yillik)"
                type="number"
                value={String(settings.bonusPoints?.birthday || 0)}
                onChange={(v) => updateBonusPoints("birthday", v)}
                autoComplete="off"
                suffix={POINTS_UNIT}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Coupon Tiers */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <div>
                  <Text as="h2" variant="headingMd">
                    Kupon Esikleri
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    Puan karsiliginda olusturulacak kuponlar
                  </Text>
                </div>
                <Button size="slim" onClick={addCouponTier}>
                  Ekle
                </Button>
              </InlineStack>
              <Divider />
              {(settings.couponTiers || []).map((coupon, index) => (
                <InlineStack key={index} gap="200" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Gereken Puan"
                      type="number"
                      value={String(coupon.points)}
                      onChange={(v) => updateCouponTier(index, "points", v)}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Indirim (TL)"
                      type="number"
                      value={String(coupon.discountTL)}
                      onChange={(v) => updateCouponTier(index, "discountTL", v)}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    tone="critical"
                    size="slim"
                    onClick={() => removeCouponTier(index)}
                  >
                    Sil
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
